import { useEffect, useState } from "react";
import { Clock, Loader2, Gift, CheckCircle2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { toast } from "sonner";

const OPTIONS = [3, 7, 14] as const;

/**
 * Inline panel inside the Billing card that lets a trialing user
 * self-serve a one-time trial extension (3, 7, or 14 days).
 */
export function TrialExtensionCard({ onExtended }: { onExtended?: () => void } = {}) {
  const { subscription, isTrialing, refresh } = useSubscription();
  const [choice, setChoice] = useState<number>(7);
  const [busy, setBusy] = useState(false);
  const [alreadyExtended, setAlreadyExtended] = useState<boolean | null>(null);
  const [phase, setPhase] = useState<"calling" | "syncing" | null>(null);
  const [confirmedEnd, setConfirmedEnd] = useState<Date | null>(null);
  const [extendedByDays, setExtendedByDays] = useState<number | null>(null);

  useEffect(() => {
    if (!subscription?.paddle_subscription_id) { setAlreadyExtended(null); return; }
    (async () => {
      const { data } = await supabase
        .from("trial_extensions" as any)
        .select("id")
        .eq("paddle_subscription_id", subscription.paddle_subscription_id)
        .maybeSingle();
      setAlreadyExtended(!!data);
    })();
  }, [subscription?.paddle_subscription_id]);

  if (!isTrialing || !subscription) return null;

  const trialEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
  const daysLeft = trialEnd
    ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const extend = async () => {
    setBusy(true);
    setPhase("calling");
    const { data, error } = await supabase.functions.invoke("subscription-extend-trial", {
      body: { days: choice, environment: getPaddleEnvironment() },
    });
    if (error || data?.error) {
      setBusy(false);
      setPhase(null);
      const msg = data?.error === "already_extended"
        ? "You've already extended this trial."
        : data?.error === "not_trialing"
          ? "Your trial has already ended."
          : (data?.error ?? error?.message ?? "Try again.");
      toast.error("Couldn't extend trial", { description: msg });
      if (data?.error === "already_extended") setAlreadyExtended(true);
      return;
    }
    setPhase("syncing");
    // Pull the fresh subscription row so we render the authoritative
    // current_period_end (the function already wrote it server-side).
    const fresh = await refresh();
    const serverEnd = (fresh as any)?.current_period_end ?? data.newTrialEnd;
    const finalEnd = new Date(serverEnd);
    setConfirmedEnd(finalEnd);
    setExtendedByDays(data.days);
    setAlreadyExtended(true);
    setBusy(false);
    setPhase(null);
    toast.success(`Trial extended by ${data.days} days`, {
      description: `New end: ${finalEnd.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`,
    });
    onExtended?.();
  };

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      {confirmedEnd && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-1.5">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <CheckCircle2 className="h-4 w-4" />
            Trial extended{extendedByDays ? ` by ${extendedByDays} days` : ""}
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              New trial end: <span className="text-foreground font-medium">
                {confirmedEnd.toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
              </span>
            </span>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2">
        <Clock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            Need more time?
            {daysLeft !== null && (
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                {daysLeft} day{daysLeft === 1 ? "" : "s"} left in your trial
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {alreadyExtended
              ? "You've already extended this trial once."
              : "Get extra days to finish evaluating PullOver. One extension per trial."}
          </p>
        </div>
      </div>

      {!alreadyExtended && (
        <>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Add days</Label>
            <div className="flex gap-2 flex-wrap">
              {OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setChoice(d)}
                  disabled={busy}
                  className={`px-3 h-9 rounded-md border text-sm transition-colors ${
                    choice === d
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-border hover:bg-muted"
                  } disabled:opacity-50`}
                >
                  +{d} days
                </button>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={extend} disabled={busy} aria-busy={busy}>
            {busy
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {phase === "syncing" ? "Confirming new end date…" : "Extending trial…"}</>
              : <><Gift className="h-4 w-4 mr-2" /> Extend trial by {choice} days</>}
          </Button>
          {busy && (
            <p className="text-[11px] text-muted-foreground">
              {phase === "syncing"
                ? "Reading the updated subscription…"
                : "Updating your subscription with Paddle…"}
            </p>
          )}
        </>
      )}
    </div>
  );
}